import React, { useEffect, useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { RiUserLine, RiPriceTag3Line, RiPaletteLine } from 'react-icons/ri';
import useAuthStore from '../store/useAuthStore';
import useStyleStore from '../store/useStyleStore';
import { applyStyle } from '../core/styleManager';
import useNotificationStore from '../store/useNotificationStore';
import useDialogStore from '../store/useDialogStore';
import { authFetch, dataFetch } from '../api';

const { FiX, FiCheck, FiTrash2, FiLogOut, FiSettings, FiPlus, FiEdit2, FiSave } = FiIcons;

const emptyProfile = {
  email: '',
  username: '',
  newPassword: '',
  confirmPassword: '',
  firstName: '',
  lastName: '',
  nip: '',
  company: '',
  phone: '',
  address: ''
};

const paidPlans = [
  {
    id: 'PRO_ONE_TIME',
    name: 'Pro Jednorazowy',
    description: 'Pełny dostęp do kreatora przez 30 dni. Opłać BLIKIEM.',
    price: '39',
    suffix: '/ jednorazowo',
    button: 'Wybierz plan'
  },
  {
    id: 'PRO_MONTHLY',
    name: 'Pro Miesięczny',
    description: 'Pełny dostęp do kreatora przez 30 dni. Rezygnujesz w dowolnej chwili.',
    price: '29',
    suffix: '/ miesiąc',
    button: 'Rozpocznij teraz',
    popular: true
  },
  {
    id: 'PRO_YEARLY',
    name: 'Pro Roczny',
    description: 'Najlepszy wybór dla biur nieruchomości. Oszczędzasz 49 zł rocznie.',
    price: '299',
    suffix: '/ rok',
    button: 'Wybierz plan'
  }
];

const planFeatures = [
  'Pełny dostęp do kreatora rzutów',
  'Nielimitowana liczba projektów',
  'Nielimitowany eksport do PNG',
  'Komercyjne wykorzystanie rzutów',
  'Biblioteka mebli i wyposażenia',
  'Dodawanie wymiarów i opisów',
  'Firmowe logo i znak wodny',
  'Wsparcie techniczne'
];

const ProfileDialog = ({ isOpen, onClose, initialTab = 'settings' }) => {
  const logout = useAuthStore((state) => state.logout);
  const user = useAuthStore((state) => state.user);
  const currentPlan = useAuthStore((state) => state.currentPlan);
  const setPlan = useAuthStore((state) => state.setPlan);
  const { styles, addStyle, updateStyle, deleteStyle, activeStyleId } = useStyleStore();
  const { addNotification } = useNotificationStore();
  const { openDialog } = useDialogStore();

  const [activeTab, setActiveTab] = useState(initialTab);
  const [editingStyle, setEditingStyle] = useState(null);
  const [formData, setFormData] = useState(emptyProfile);
  const [profileId, setProfileId] = useState(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const userId = localStorage.getItem('user_id');
    setActiveTab(initialTab);

    if (!userId) {
      setProfileId(null);
      setFormData(emptyProfile);
      return;
    }

    const loadProfile = async () => {
      setIsLoadingProfile(true);
      const response = await dataFetch(`read/profiles?owner_id=${encodeURIComponent(userId)}`);

      if (response?.status === 'success') {
        const profile = Array.isArray(response?.data) ? response.data[0] : null;

        setProfileId(profile?.id ?? null);
        setFormData({
          email: user?.email ?? '',
          username: profile?.username ?? user?.name ?? '',
          newPassword: '',
          confirmPassword: '',
          firstName: profile?.first_name ?? '',
          lastName: profile?.last_name ?? '',
          nip: profile?.nip ?? '',
          company: profile?.company ?? '',
          phone: profile?.phone ?? '',
          address: profile?.address ?? ''
        });
      } else {
        setProfileId(null);
        setFormData((previous) => ({
          ...previous,
          email: user?.email ?? ''
        }));
        addNotification('error', response?.error ?? 'Nie udało się pobrać danych profilu.');
      }

      setIsLoadingProfile(false);
    };

    loadProfile();
  }, [isOpen, initialTab, user?.email, user?.name, addNotification]);

  if (!isOpen) return null;

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const getUserId = () => {
    const userId = localStorage.getItem('user_id');

    if (!userId) {
      addNotification('error', 'Sesja wygasła. Zaloguj się ponownie.');
      return null;
    }

    return userId;
  };

  const handleSaveProfile = async () => {
    const userId = getUserId();

    if (!userId || isSavingProfile) return;

    if (!formData.username.trim()) {
      addNotification('error', 'Podaj nazwę użytkownika.');
      return;
    }

    if (formData.newPassword || formData.confirmPassword) {
      if (formData.newPassword !== formData.confirmPassword) {
        addNotification('error', 'Nowe hasła nie są takie same.');
        return;
      }

      addNotification('info', 'Zmianę hasła wykonaj przez funkcję odzyskiwania hasła na ekranie logowania.');
      return;
    }

    const payload = {
      user_id: userId,
      username: formData.username.trim(),
      first_name: formData.firstName.trim(),
      last_name: formData.lastName.trim(),
      nip: formData.nip.trim(),
      company: formData.company.trim(),
      phone: formData.phone.trim(),
      address: formData.address.trim(),
      updated_at: Date.now()
    };

    setIsSavingProfile(true);

    const response = await dataFetch(
      profileId
        ? `update/profiles/${profileId}?owner_id=${encodeURIComponent(userId)}`
        : `create/profiles?owner_id=${encodeURIComponent(userId)}`,
      {
        method: profileId ? 'PUT' : 'POST',
        body: JSON.stringify(payload)
      }
    );

    setIsSavingProfile(false);

    if (response?.status !== 'success') {
      addNotification('error', response?.error ?? 'Nie udało się zapisać danych profilu.');
      return;
    }

    setProfileId((previous) => previous ?? response?.data?.id ?? null);
    addNotification('success', 'Zmiany zostały zapisane.');
  };

  const handleDeleteAccount = () => {
    if (isDeletingAccount) return;

    openDialog({
      title: 'Usuń konto',
      message: 'Czy na pewno chcesz trwale usunąć konto oraz wszystkie zapisane rzuty? Tej operacji nie można cofnąć.',
      confirmText: 'Usuń konto',
      variant: 'danger',
      onConfirm: async () => {
        const userId = getUserId();

        if (!userId) return;

        setIsDeletingAccount(true);

        const collections = [
          { table: 'projects', path: 'read/projects' },
          { table: 'profiles', path: 'read/profiles' },
          { table: 'subscriptions', path: 'read/subscriptions' }
        ];

        for (const collection of collections) {
          const response = await dataFetch(
            `${collection.path}?owner_id=${encodeURIComponent(userId)}&user_id=${encodeURIComponent(userId)}`,
            { method: 'GET' }
          );

          if (response?.status !== 'success') continue;

          const records = Array.isArray(response?.data) ? response.data : [];

          await Promise.allSettled(
            records
              .map((record) => record?.id)
              .filter((id) => id !== null && id !== undefined)
              .map((id) => dataFetch(
                `delete/${collection.table}/${id}?owner_id=${encodeURIComponent(userId)}`,
                { method: 'DELETE' }
              ))
          );
        }

        const accountResponse = await authFetch('delete-user', {
          method: 'POST',
          body: JSON.stringify({})
        });

        if (accountResponse?.status !== 'success') {
          setIsDeletingAccount(false);
          addNotification('error', accountResponse?.error ?? 'Nie udało się usunąć konta.');
          return;
        }

        localStorage.removeItem('user_id');
        logout();
        onClose();
        setIsDeletingAccount(false);
        addNotification('success', 'Konto zostało usunięte.');
      }
    });
  };

  const handleLogout = () => {
    logout();
    localStorage.removeItem('user_id');
    onClose();
    addNotification('info', 'Wylogowano pomyślnie.');
  };

  const handlePlanChange = (plan) => {
    setPlan(plan);
    addNotification('success', `Plan zmieniony na ${plan}`);
  };

  const handleEditStyle = (style) => {
    setEditingStyle({ ...style });
  };

  const handleAddStyle = () => {
    setEditingStyle({
      id: null,
      name: 'Nowy Styl',
      isSystem: false,
      walls: { color: '#000000', thickness: 16 },
      rooms: { fillColor: '#ffffff', fillOpacity: 50 },
      objects: { stroke: '#000000', fill: '#ffffff' }
    });
  };

  const handleSaveStyle = () => {
    if (!editingStyle) return;

    if (editingStyle.id) {
      updateStyle(editingStyle.id, editingStyle);
      addNotification('success', 'Styl zaktualizowany.');
    } else {
      addStyle(editingStyle);
      addNotification('success', 'Nowy styl dodany.');
    }

    setEditingStyle(null);
  };

  const handleDeleteStyle = (id) => {
    openDialog({
      title: 'Usuń Styl',
      message: 'Czy na pewno chcesz trwale usunąć ten styl?',
      confirmText: 'Usuń',
      variant: 'danger',
      onConfirm: () => {
        deleteStyle(id);
        if (editingStyle?.id === id) setEditingStyle(null);
        addNotification('info', 'Styl został usunięty.');
      }
    });
  };

  const handleApplyStyle = (styleId) => {
    applyStyle(styleId);
    addNotification('success', 'Styl został zastosowany do projektu.');
  };

  const renderStylesTab = () => {
    if (editingStyle) {
      return (
        <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
          <div className="flex items-center justify-between border-b pb-4">
            <h3 className="text-lg font-bold text-gray-800">Edycja Stylu</h3>
            <div className="flex gap-2">
              <button onClick={() => setEditingStyle(null)} className="px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded-lg">Anuluj</button>
              <button onClick={handleSaveStyle} className="px-3 py-1.5 bg-[#1E2937] text-white rounded-lg flex items-center gap-2">
                <SafeIcon icon={FiSave} /> Zapisz
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nazwa stylu</label>
                <input
                  type="text"
                  value={editingStyle.name}
                  onChange={(event) => setEditingStyle({ ...editingStyle, name: event.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E2937] focus:border-transparent"
                />
              </div>

              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-700 mb-3 text-sm uppercase tracking-wide">Ściany</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Grubość (cm)</label>
                    <input
                      type="number"
                      value={editingStyle.walls.thickness}
                      onChange={(event) => setEditingStyle({
                        ...editingStyle,
                        walls: { ...editingStyle.walls, thickness: parseInt(event.target.value, 10) || 16 }
                      })}
                      className="w-full px-2 py-1.5 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Kolor</label>
                    <input
                      type="color"
                      value={editingStyle.walls.color}
                      onChange={(event) => setEditingStyle({
                        ...editingStyle,
                        walls: { ...editingStyle.walls, color: event.target.value }
                      })}
                      className="h-9 w-full rounded cursor-pointer"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-700 mb-3 text-sm uppercase tracking-wide">Pomieszczenia</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Wypełnienie</label>
                    <input
                      type="color"
                      value={editingStyle.rooms.fillColor}
                      onChange={(event) => setEditingStyle({
                        ...editingStyle,
                        rooms: { ...editingStyle.rooms, fillColor: event.target.value }
                      })}
                      className="h-9 w-full rounded cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Przezroczystość ({editingStyle.rooms.fillOpacity}%)</label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={editingStyle.rooms.fillOpacity}
                      onChange={(event) => setEditingStyle({
                        ...editingStyle,
                        rooms: { ...editingStyle.rooms, fillOpacity: parseInt(event.target.value, 10) }
                      })}
                      className="w-full mt-2"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-700 mb-3 text-sm uppercase tracking-wide">Meble i Kształty</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Kolor linii (obrys)</label>
                    <input
                      type="color"
                      value={editingStyle.objects.stroke}
                      onChange={(event) => setEditingStyle({
                        ...editingStyle,
                        objects: { ...editingStyle.objects, stroke: event.target.value }
                      })}
                      className="h-9 w-full rounded cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Wypełnienie domyślne (tylko kształty)</label>
                    <input
                      type="color"
                      value={editingStyle.objects.fill}
                      onChange={(event) => setEditingStyle({
                        ...editingStyle,
                        objects: { ...editingStyle.objects, fill: event.target.value }
                      })}
                      className="h-9 w-full rounded cursor-pointer"
                    />
                  </div>
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-4 flex flex-col items-center justify-center bg-white h-40">
                <span className="text-xs text-gray-400 mb-2">PODGLĄD</span>
                <div
                  className="relative w-32 h-24 border-4 flex items-center justify-center"
                  style={{
                    borderColor: editingStyle.walls.color,
                    backgroundColor: editingStyle.rooms.fillColor
                  }}
                >
                  <div className="absolute inset-0 bg-white" style={{ opacity: 1 - (editingStyle.rooms.fillOpacity / 100) }} />
                  <div
                    className="w-10 h-10 border-2 relative z-10"
                    style={{
                      borderColor: editingStyle.objects.stroke,
                      backgroundColor: editingStyle.objects.fill
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-bold text-gray-800">Twoje Style</h3>
          <button onClick={handleAddStyle} className="px-4 py-2 bg-[#1E2937] text-white rounded-lg flex items-center gap-2 hover:bg-gray-800 transition-colors">
            <SafeIcon icon={FiPlus} /> Dodaj własny styl
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(styles ?? []).map((style) => (
            <div key={style.id} className="border border-gray-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow bg-white group relative">
              <div className="h-32 bg-gray-50 relative flex items-center justify-center border-b border-gray-100">
                <div className="w-20 h-20 border-4 shadow-sm z-10" style={{ borderColor: style.walls.color, backgroundColor: style.rooms.fillColor }} />
                <div className="absolute inset-0 opacity-20" style={{ backgroundColor: style.rooms.fillColor }} />
              </div>
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-bold text-gray-800">{style.name}</h4>
                  {style.id === activeStyleId && <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold">AKTYWNY</span>}
                </div>
                <div className="flex gap-2 mt-4 text-sm">
                  <button onClick={() => handleApplyStyle(style.id)} className="flex-1 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-center font-medium transition-colors">Zastosuj</button>
                  <button onClick={() => handleEditStyle(style)} className="p-1.5 text-gray-500 hover:text-[#1E2937] hover:bg-gray-100 rounded transition-colors" title="Edytuj">
                    <SafeIcon icon={FiEdit2} />
                  </button>
                  {!style.isSystem && (
                    <button onClick={() => handleDeleteStyle(style.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title="Usuń">
                      <SafeIcon icon={FiTrash2} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const pricingData = [
    { feature: 'Dla kogo', free: 'Testy, sprawdzenie możliwości programu', pro: 'Dla osób sprzedających lub wynajmujących mieszkanie', agency: 'Biura nieruchomości, deweloperzy, zespoły projektowe, agencje' },
    { feature: 'Liczba użytkowników', free: '1', pro: '1', agency: '3' },
    { feature: 'Subskrypcja', free: 'Darmowy', pro: 'Jednorazowo', agency: 'Miesięcznie / Rocznie' },
    { feature: 'Liczba eksportów / okres trwania planu', free: '3 eksporty', pro: '30 eksportów', agency: '30 / 365 dni' },
    { feature: 'Liczba zapisanych rzutów', free: '1', pro: '10', agency: 'Bez limitu' },
    { feature: 'Znak wodny', free: 'Tak', pro: 'X', agency: 'X' },
    { feature: 'Hi-res JPG/PNG', free: 'X', pro: '20 eksportów', agency: 'Bez limitu' },
    { feature: 'PDF wektorowy', free: 'X', pro: 'X', agency: 'Bez limitu' },
    { feature: 'Fotorealistyczny render AI', free: '✕', pro: '5', agency: '50 miesięcznie' },
    { feature: 'Style projektu', free: '✕', pro: '✕', agency: 'Bez limitu' },
    { feature: 'Cena netto', free: '0 zł', pro: '29 zł', agency: '59 zł / 412 zł', isPrice: true }
  ];

  const renderCellContent = (content) => {
    if (content === 'X') return <span className="text-gray-300 font-bold">✕</span>;
    if (content === 'Tak') return <SafeIcon icon={FiCheck} className="text-green-500 mx-auto" />;
    return content;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-100 flex-shrink-0 bg-white z-10">
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <SafeIcon icon={RiUserLine} className="text-xl" />
            Mój profil
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <SafeIcon icon={FiX} />
          </button>
        </div>

        <div className="flex border-b border-gray-100 bg-gray-50/50">
          <button onClick={() => setActiveTab('settings')} className={`px-6 py-3 text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === 'settings' ? 'text-[#1E2937] border-b-2 border-[#1E2937] bg-white' : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'}`}>
            <SafeIcon icon={FiSettings} />
            USTAWIENIA KONTA
          </button>
          <button onClick={() => setActiveTab('pricing')} className={`px-6 py-3 text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === 'pricing' ? 'text-[#1E2937] border-b-2 border-[#1E2937] bg-white' : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'}`}>
            <SafeIcon icon={RiPriceTag3Line} />
            CENNIK
          </button>
          {currentPlan === 'AGENCY' && (
            <button onClick={() => setActiveTab('styles')} className={`px-6 py-3 text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === 'styles' ? 'text-[#1E2937] border-b-2 border-[#1E2937] bg-white' : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'}`}>
              <SafeIcon icon={RiPaletteLine} />
              MOJE STYLE
            </button>
          )}
        </div>

        <div className="p-6 overflow-y-auto flex-1 bg-white">
          {activeTab === 'settings' && (
            <div className="space-y-6 max-w-2xl mx-auto">
              <div className="space-y-6">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider border-b pb-2">Ustawienia Konta</h3>

                {isLoadingProfile && <p className="text-sm text-gray-500">Pobieranie danych użytkownika...</p>}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">E-mail</label>
                    <input type="email" name="email" value={formData.email} disabled className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed outline-none" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">Nazwa użytkownika</label>
                    <input type="text" name="username" value={formData.username} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E2937] focus:border-transparent outline-none" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">Nowe hasło</label>
                    <input type="password" name="newPassword" value={formData.newPassword} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E2937] focus:border-transparent outline-none" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">Potwierdź nowe hasło</label>
                    <input type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E2937] focus:border-transparent outline-none" />
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg flex items-center justify-between border border-gray-200">
                  <div>
                    <span className="text-sm text-gray-500">Twój plan</span>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="font-bold text-gray-800 text-lg">{currentPlan === 'AGENCY' ? 'PRO AGENCY' : currentPlan}</span>
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded-full">AKTYWNY</span>
                    </div>
                  </div>
                  <button onClick={() => setActiveTab('pricing')} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm">Zmień plan</button>
                </div>

                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider border-b pb-2 mt-8">Dane Osobowe / Firmowe</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">Imię</label>
                    <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E2937] focus:border-transparent outline-none" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">Nazwisko</label>
                    <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E2937] focus:border-transparent outline-none" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">NIP</label>
                    <input type="text" name="nip" value={formData.nip} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E2937] focus:border-transparent outline-none" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">Firma</label>
                    <input type="text" name="company" value={formData.company} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E2937] focus:border-transparent outline-none" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">Telefon</label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E2937] focus:border-transparent outline-none" />
                  </div>
                  <div className="space-y-1 md:col-span-2">
                    <label className="text-sm font-medium text-gray-700">Adres</label>
                    <input type="text" name="address" value={formData.address} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E2937] focus:border-transparent outline-none" />
                  </div>
                </div>

                <div className="pt-6 mt-6 border-t border-gray-100">
                  <button
                    onClick={() => addNotification('info', 'Anulowanie subskrypcji zostanie udostępnione po podłączeniu operatora płatności.')}
                    className="flex items-center gap-2 text-red-600 hover:text-red-700 font-medium px-4 py-2 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiTrash2} />
                    Anuluj subskrypcję
                  </button>
                  <p className="text-xs text-red-500 mt-2 px-4">Po anulowaniu użytkownik od razu wróci do planu Free.</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'pricing' && (
            <div className="animate-in fade-in zoom-in duration-200">
              <h3 className="text-xl font-bold text-gray-800 mb-6 text-center">WYBIERZ PLAN</h3>

              <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                {paidPlans.map((plan) => (
                  <div
                    key={plan.id}
                    className={`relative flex min-h-[520px] flex-col rounded-2xl border bg-white p-7 ${
                      plan.popular
                        ? 'border-2 border-[#1E2937] shadow-xl'
                        : 'border-gray-200'
                    }`}
                  >
                    {plan.popular && (
                      <span className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#1E2937] px-5 py-1.5 text-xs font-bold tracking-wide text-white">
                        POPULARNY
                      </span>
                    )}

                    <h4 className="text-2xl font-bold text-[#1E2937]">{plan.name}</h4>
                    <p className="mt-4 min-h-[72px] text-base leading-7 text-slate-500">
                      {plan.description}
                    </p>

                    <div className="mt-6 flex items-baseline gap-2 text-[#1E2937]">
                      <span className="text-6xl font-medium">{plan.price}</span>
                      <span className="text-2xl">zł</span>
                      <span className="text-sm text-slate-500">{plan.suffix}</span>
                    </div>

                    <div className="my-8 border-t border-gray-100" />

                    <ul className="space-y-4 text-base text-slate-600">
                      {planFeatures.map((feature) => (
                        <li key={feature} className="flex gap-3">
                          <SafeIcon icon={FiCheck} className="mt-1 shrink-0 text-sm text-slate-400" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <button
                      type="button"
                      disabled
                      title="Płatności zostaną udostępnione po podłączeniu operatora płatności"
                      className="mt-auto cursor-not-allowed rounded-xl border border-gray-200 bg-gray-100 px-5 py-4 text-base font-bold text-gray-400"
                    >
                      Płatności wkrótce dostępne
                    </button>
                  </div>
                ))}
              </div>

              <p className="mt-6 text-center text-xs text-gray-500">
                Plan zostanie aktywowany dopiero po potwierdzeniu poprawnej płatności po stronie backendu.
              </p>
            </div>
          )}

          {activeTab === 'styles' && renderStylesTab()}
        </div>

        <div className="p-4 bg-gray-50 border-t border-gray-100 flex justify-between items-center flex-shrink-0">
          <button onClick={handleLogout} className="px-6 py-2 bg-white text-black border border-black rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 font-medium">
            <SafeIcon icon={FiLogOut} />
            Wyloguj
          </button>
          <button onClick={handleSaveProfile} disabled={activeTab !== 'settings' || isSavingProfile || isLoadingProfile} className="px-6 py-2 bg-[#1E2937] text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-2 font-medium disabled:opacity-50">
            <SafeIcon icon={FiCheck} />
            Zapisz zmiany
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileDialog;